import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { ApiError } from "@/types/api";

/**
 * Turns an RTK Query rejection into something worth showing a person.
 *
 * The API answers a refused mutation with `{ error, message, details }` where
 * `message` is written for a human — "3 admins still hold this role. Remove it
 * from them first." That message is the whole point of the guards on the
 * server, and dropping it in favour of a generic "Request failed" throws away
 * the only part of the response that tells the user what to do next.
 *
 * The shape has to be probed rather than cast: an RTK error is also how a
 * network failure and a serialisation error arrive, and neither carries a
 * body.
 */

function isFetchError(error: unknown): error is FetchBaseQueryError {
  return typeof error === "object" && error !== null && "status" in error;
}

function bodyOf(error: unknown): Partial<ApiError> | null {
  if (!isFetchError(error)) return null;
  const data = (error as { data?: unknown }).data;
  return typeof data === "object" && data !== null ? (data as Partial<ApiError>) : null;
}

/**
 * Codes whose wording the panel owns rather than the API. Checked before the
 * server's message, so the same refusal reads the same on every screen.
 */
const MESSAGES: Record<string, string> = {
  SUPER_ADMIN_ROLE_RESERVED: "This role can't be assigned.",
  SUPER_ADMIN_PROTECTED: "The Super Admin account can't be changed or deleted.",
};

export function apiErrorMessage(error: unknown, fallback = "Something went wrong."): string {
  if (!error) return fallback;

  const body = bodyOf(error);
  const known = typeof body?.error === "string" ? MESSAGES[body.error] : undefined;
  if (known) return known;

  if (typeof body?.message === "string" && body.message.length > 0) {
    // A validation failure names the field in `details`, and the top-level
    // message ("Request validation failed") does not. Surfacing the first
    // field error is the difference between a usable form and a shrug.
    const field = firstValidationMessage(body.details);
    return field ? `${field}` : body.message;
  }

  /*
    Past this point the API said nothing usable, so these are written for
    whoever is looking at the screen rather than for whoever is reading the
    logs. A status code in the interface is a number the user cannot act on and
    will read as the product being broken; the code is in the console and in
    the network tab, where the person who can act on it already is.
  */
  if (isFetchError(error)) {
    if (error.status === "FETCH_ERROR") return "Can't reach the server.";
    if (error.status === "TIMEOUT_ERROR") return "That took too long. Try again.";

    if (typeof error.status === "number") {
      if (error.status === 404) return "That isn't available.";
      if (error.status === 409) return "Someone else changed this first. Reload and try again.";
      if (error.status === 429) return "Too many requests. Wait a moment and try again.";
      if (error.status >= 500) return "Something went wrong on our side. Try again in a moment.";
      return fallback;
    }
  }

  return fallback;
}

/**
 * A failure of the server or the connection rather than a refusal of what was
 * sent: a 5xx, a network error or a timeout. Nothing in the form can fix it,
 * so it is reported in a toast instead of beside the fields.
 */
export function isServerFailure(error: unknown): boolean {
  if (!isFetchError(error)) return false;
  if (error.status === "FETCH_ERROR" || error.status === "TIMEOUT_ERROR") return true;
  return typeof error.status === "number" && error.status >= 500;
}

/** A 404 — for an endpoint the API may not have shipped yet, among others. */
export function isNotFound(error: unknown): boolean {
  return isFetchError(error) && error.status === 404;
}

/**
 * `apiErrorMessage` for calls against a single role. A 404 there usually means
 * an old link to the Super Admin role, which `/roles` no longer exposes.
 */
export function roleErrorMessage(error: unknown, fallback?: string): string {
  if (isFetchError(error) && error.status === 404) return "Role not found";
  return apiErrorMessage(error, fallback);
}

/** The stable machine code, for branching on a specific refusal. */
export function apiErrorCode(error: unknown): string | null {
  const body = bodyOf(error);
  return typeof body?.error === "string" ? body.error : null;
}

/**
 * Pulls the first field-level message out of a Zod validation payload.
 *
 * The API sends `details` as the array the Fastify/Zod bridge produces, whose
 * entries carry `instancePath` ("/permissions/0") and `message`. Only the
 * first is shown: a form that lists eight simultaneous complaints gets read as
 * none of them.
 */
function firstValidationMessage(details: unknown): string | null {
  if (!Array.isArray(details) || details.length === 0) return null;

  const first = details[0] as { instancePath?: unknown; message?: unknown };
  if (typeof first.message !== "string") return null;

  const path = typeof first.instancePath === "string" ? first.instancePath.replace(/^\//, "") : "";

  return path ? `${path}: ${first.message}` : first.message;
}
